import { useLife } from '@/state/life';
import { useMarkets } from '@/state/markets';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * The Affair mini-arc.
 *
 * Triggered when wife rep is recovering from divorce arc but you're also
 * matched with a "Sloane" through the dating-app contact. 3 tension
 * decisions decide whether you get caught (TMZ post + tribal swing +
 * second divorce filing) or thread the needle.
 */

interface Beat {
  text: string;
  options: { label: string; effect: () => number }[]; // returns "discretion score" delta
}

const BEATS: Beat[] = [
  {
    text: 'SLOANE: "drinks tonight at the Carlyle? you said you would."',
    options: [
      { label: 'go. cab there. leave phone at home.', effect: () => { useLife.getState().bumpSanity(-2); return 1; } },
      { label: 'go. take the chauffeured car.', effect: () => { useLife.getState().bumpRelationship('wife', -8); return -1; } },
      { label: 'cancel. send flowers and a vague apology.', effect: () => { useLife.getState().bumpSanity(2); return 2; } },
    ],
  },
  {
    text: 'YOUR WIFE just texted: "what time will you be home?" you\'re at the Carlyle. options:',
    options: [
      { label: '"working late. don\'t wait up."', effect: () => { useLife.getState().bumpRelationship('wife', -4); useLife.getState().bumpSanity(-3); return 0; } },
      { label: '"i\'m out with a client. home by 11."', effect: () => { useLife.getState().bumpRelationship('wife', -2); return 1; } },
      { label: 'leave immediately. cab home.', effect: () => { useLife.getState().bumpRelationship('wife', 4); return 2; } },
    ],
  },
  {
    text: 'TMZ DM: "we\'ve got pics. \$100K and we kill the story. \$200K and we kill the source too."',
    options: [
      { label: 'pay \$100K to kill the story', effect: () => {
        useMarkets.getState().addCash(-100_000);
        useLife.getState().bumpFbiHeat(2);
        return 2;
      } },
      { label: 'don\'t pay. let them publish. dare them.', effect: () => { return -3; } },
      { label: 'forward to your PR flack. let her handle it.', effect: () => {
        if (usePhone.getState().contacts['pr-flack']) {
          useMarkets.getState().addCash(-30_000);
          return 1;
        }
        return -2;
      } },
    ],
  },
];

let discretionScore = 0;

export function startAffairArc(): void {
  discretionScore = 0;
  useMeta.getState().recordLore('arc-affair', 'The Affair');

  // Add Sloane to contacts if not present
  if (!usePhone.getState().contacts['sloane']) {
    usePhone.setState((s) => ({
      ...s,
      contacts: { ...s.contacts, 'sloane': { id: 'sloane', displayName: 'Sloane' } },
    }));
  }

  useCinematic.getState().fire({
    id: `affair-${Date.now()}`,
    type: 'breaking',
    headline: 'YOU MATCHED WITH SLOANE.',
    subhead: 'she\'s 31, "in finance," and reportedly does not exist on LinkedIn. 3 decisions in your near future. one wife. one TMZ DM.',
    durationMs: 5500,
  });
  sounds.notifyAlert();

  for (let i = 0; i < BEATS.length; i++) {
    const b = BEATS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = b.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `affair-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < b.options.length; j++) {
        const id = `affair-q${i}-${ids[j]}`;
        const opt = b.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          discretionScore += opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: 'sloane',
        fromNpc: 'sloane',
        text: b.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `affair: moment ${i + 1}/3` });
      sounds.notifyAlert();
    }, 5_000 + i * 30_000);
  }

  setTimeout(() => {
    if (discretionScore >= 4) {
      useNotifications.getState().push({
        surfaceId: 'phone', severity: 'info',
        text: 'sloane: "this was fun. i\'m not asking for anything."',
      });
      useLife.getState().bumpSanity(2);
    } else {
      useCinematic.getState().fire({
        id: `affair-exposed-${Date.now()}`,
        type: 'breaking',
        headline: 'TMZ POSTED THE PHOTOS.',
        subhead: 'caption: "well-known retail trader spotted at the Carlyle with a woman who is not his wife." comments are 50/50 cheering/threats. wife has read the article.',
        durationMs: 6500,
      });
      useLife.getState().bumpRelationship('wife', -40);
      usePower.getState().bumpRep('press', -8);
      usePower.getState().bumpNotoriety(20);
      useXClone.getState().receivePost({
        authorHandle: 'TMZTabloidTrader', authorTribe: 'press',
        text: "EXCLUSIVE: photos of a 'well-known retail trader' at the Carlyle with a woman the public has identified as 'Sloane.' wife has not commented. wife's lawyer has commented. extensively.",
        likes: 18000, reposts: 5400,
      });
    }
  }, 5_000 + BEATS.length * 30_000 + 5_000);
}
